import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import LanguageSwitcher from './LanguageSwitcher';

type RodoSection = {
  heading: string;
  paragraphs: string[];
};

type RodoContent = {
  homeLabel: string;
  title: string;
  version: string;
  sections: RodoSection[];
  footer: string;
};

const RODO_CONTENT: { pl: RodoContent; en: RodoContent } = {
  pl: {
    homeLabel: 'Strona glowna',
    title: 'Polityka prywatnosci (RODO)',
    version: 'Wersja 1.2 | Data obowiazywania: 2026-03-07',
    sections: [
      {
        heading: '1. Administrator danych',
        paragraphs: [
          'Administratorem danych osobowych przetwarzanych w ramach platformy Alenis jest Alenis. W sprawach dotyczacych ochrony danych mozna kontaktowac sie pod adresem: alenis.services@gmail.com.',
        ],
      },
      {
        heading: '2. Cele i podstawy przetwarzania',
        paragraphs: [
          'Dane przetwarzamy w celu swiadczenia uslug platformy, obslugi konta i zgloszen uzytkownikow, zapewnienia bezpieczenstwa systemu, realizacji obowiazkow prawnych oraz dochodzenia lub obrony roszczen.',
          'Podstawa przetwarzania jest m.in. wykonanie umowy lub dzialania przed jej zawarciem, prawnie uzasadniony interes administratora, zgoda uzytkownika (gdy wymagana), a takze obowiazki wynikajace z przepisow prawa.',
        ],
      },
      {
        heading: '3. Zakres przetwarzanych danych',
        paragraphs: [
          'Przetwarzane moga byc w szczegolnosci: dane identyfikacyjne i kontaktowe (np. imie, e-mail), dane techniczne (np. adres IP, logi systemowe), tresc przeslanych dokumentow, w tym faktur, dane zawarte w danych/fakturach wysylanych przez uzytkownika do dalszego przetwarzania oraz metadane zwiazane z korzystaniem z platformy.',
        ],
      },
      {
        heading: '4. Odbiorcy danych',
        paragraphs: [
          'Dane moga byc powierzane podmiotom wspierajacym swiadczenie uslugi (np. hosting, infrastruktura chmurowa, utrzymanie systemow), wylacznie w zakresie niezbednym do realizacji celow przetwarzania i przy zachowaniu odpowiednich zabezpieczen.',
        ],
      },
      {
        heading: '5. Okres przechowywania danych',
        paragraphs: [
          'Dane przechowujemy przez okres niezbedny do realizacji celow przetwarzania, a nastepnie przez czas wymagany przepisami prawa lub do uplywu terminow przedawnienia roszczen.',
        ],
      },
      {
        heading: '6. Prawa osoby, ktorej dane dotycza',
        paragraphs: [
          'Uzytkownikowi przysluguje prawo dostepu do danych, ich sprostowania, usuniecia, ograniczenia przetwarzania, przenoszenia danych, sprzeciwu wobec przetwarzania oraz cofniecia zgody (jezeli przetwarzanie odbywa sie na jej podstawie), bez wplywu na zgodnosc z prawem przetwarzania dokonanego przed cofnieniem.',
          'Uzytkownik ma rowniez prawo wniesienia skargi do Prezesa Urzedu Ochrony Danych Osobowych.',
        ],
      },
      {
        heading: '7. Bezpieczenstwo danych',
        paragraphs: [
          'Stosujemy srodki organizacyjne i techniczne adekwatne do ryzyka, w tym kontrole dostepu, mechanizmy ochrony transmisji i monitorowanie bezpieczenstwa. Mimo stosowanych zabezpieczen nie mozna calkowicie wykluczyc ryzyk wlasciwych dla srodowiska internetowego.',
        ],
      },
      {
        heading: '8. Postanowienia koncowe',
        paragraphs: [
          'Polityka moze byc aktualizowana. Aktualna wersja jest publikowana na tej stronie.',
          'Zasady korzystania z platformy okresla rowniez Regulamin dostepny pod adresem /regulamin.',
        ],
      },
    ],
    footer: '© 2025 Alenis. Wszystkie prawa zastrzezone.',
  },
  en: {
    homeLabel: 'Home',
    title: 'Privacy Policy (GDPR)',
    version: 'Version 1.2 | Effective date: 2026-03-07',
    sections: [
      {
        heading: '1. Data Controller',
        paragraphs: [
          'The controller of personal data processed within the Alenis platform is Alenis. For data protection matters, contact: alenis.services@gmail.com.',
        ],
      },
      {
        heading: '2. Purposes and Legal Bases of Processing',
        paragraphs: [
          'We process data to provide platform services, handle user accounts and requests, ensure system security, fulfill legal obligations, and pursue or defend legal claims.',
          'Legal bases include performance of a contract or pre-contractual steps, the controller\'s legitimate interest, user consent (where required), and obligations under applicable law.',
        ],
      },
      {
        heading: '3. Scope of Processed Data',
        paragraphs: [
          'Processed data may include identification and contact data (e.g., name, e-mail), technical data (e.g., IP address, system logs), content of uploaded documents, including invoices, data contained in data/invoices sent by the user for further processing, and metadata related to platform usage.',
        ],
      },
      {
        heading: '4. Data Recipients',
        paragraphs: [
          'Data may be entrusted to entities supporting service delivery (e.g., hosting, cloud infrastructure, system maintenance) only to the extent necessary for processing purposes and with appropriate safeguards.',
        ],
      },
      {
        heading: '5. Data Retention Period',
        paragraphs: [
          'We retain data for the period necessary to achieve processing purposes and then for the period required by law or until expiration of limitation periods for claims.',
        ],
      },
      {
        heading: '6. Data Subject Rights',
        paragraphs: [
          'Users have the right to access, rectify, erase, restrict processing, transfer data, object to processing, and withdraw consent (if processing is based on consent), without affecting lawfulness of processing carried out before withdrawal.',
          'Users also have the right to lodge a complaint with the President of the Personal Data Protection Office (PUODO).',
        ],
      },
      {
        heading: '7. Data Security',
        paragraphs: [
          'We apply organizational and technical measures appropriate to risk, including access control, transmission protection mechanisms, and security monitoring. Despite safeguards, risks inherent to internet environments cannot be fully eliminated.',
        ],
      },
      {
        heading: '8. Final Provisions',
        paragraphs: [
          'This policy may be updated. The current version is published on this page.',
          'Rules for using the platform are also set out in the Terms available at /regulamin.',
        ],
      },
    ],
    footer: '© 2025 Alenis. All rights reserved.',
  },
};

const RodoPage: React.FC = () => {
  const { i18n } = useTranslation();
  const isPolish = i18n.language.toLowerCase().startsWith('pl');
  const content = isPolish ? RODO_CONTENT.pl : RODO_CONTENT.en;

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-900 via-slate-800 to-blue-900 text-white">
      <header className="relative z-10 bg-white/5 backdrop-blur-lg border-b border-white/10">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-gradient-to-br from-blue-400 to-cyan-500 rounded-xl flex items-center justify-center shadow-lg">
                <span className="text-white font-bold text-lg">A</span>
              </div>
              <span className="text-2xl font-bold bg-gradient-to-r from-white to-blue-200 bg-clip-text text-transparent">Alenis</span>
            </Link>
            <div className="flex items-center gap-4">
              <LanguageSwitcher />
              <Link
                to="/"
                className="px-6 py-3 bg-gradient-to-r from-blue-600 to-cyan-600 text-white rounded-xl hover:from-blue-700 hover:to-cyan-700 transition-all shadow-lg hover:shadow-xl"
              >
                {content.homeLabel}
              </Link>
            </div>
          </div>
        </div>
      </header>

      <main className="relative z-10 py-16">
        <div className="max-w-5xl mx-auto px-6">
          <article className="bg-white text-slate-900 rounded-2xl p-8 md:p-10 leading-7 space-y-6 shadow-2xl border border-slate-200">
            <header className="space-y-2">
              <h1 className="text-3xl font-bold">{content.title}</h1>
              <p className="text-sm text-slate-500">{content.version}</p>
            </header>

            {content.sections.map((section) => (
              <section className="space-y-3" key={section.heading}>
                <h2 className="text-xl font-semibold">{section.heading}</h2>
                {section.paragraphs.map((paragraph) => (
                  <p key={`${section.heading}-${paragraph.slice(0, 24)}`}>
                    {paragraph.includes('/regulamin') ? (
                      <>
                        {paragraph.replace('/regulamin', '')}
                        <Link to="/regulamin" className="text-blue-700 underline ml-1">/regulamin</Link>
                      </>
                    ) : paragraph.includes('alenis.services@gmail.com') ? (
                      <>
                        {paragraph.replace('alenis.services@gmail.com', '')}
                        <a href="mailto:alenis.services@gmail.com" className="text-blue-700 underline">alenis.services@gmail.com</a>
                      </>
                    ) : (
                      paragraph
                    )}
                  </p>
                ))}
              </section>
            ))}
          </article>
        </div>
      </main>

      <footer className="relative z-10 bg-slate-900/80 backdrop-blur-sm border-t border-white/10 py-8">
        <div className="max-w-7xl mx-auto px-6 text-center">
          <div className="text-slate-400 text-sm">{content.footer}</div>
        </div>
      </footer>
    </div>
  );
};

export default RodoPage;
